// components/SearchDetailPane.jsx — OnSumn Spotlight detail preview.
//
// Pure-presentational. Used as the right column of the desktop search panel
// AND as the body of the mobile SearchDetailPopover. No state, no portal —
// the parent owns mounting. (The chips are the exception: MERCH / SHARE /
// ENTRY each hold their own triggerRect and mount their popover via portal,
// same as the homepage cards.)
//
// `item` is one of:
//   { kind: 'event',  event: <EventObject>  }
//   { kind: 'venue',  venue: <VenueObject>, eventCount: number }
//
// Window-attached at file bottom per OnSumn convention.

// Venue entry rules — the Spotlight pane is the THIRD client surface for
// the rules rail (Events.jsx card footer, VenueEventList row cluster), on
// the same ONE gate. Vite resolves this into the shared chunk, so the
// module-level fetch memo is one read per page (see the gate's header).
import {
  policyChipForEvent, useVenuePolicies,
  rulesChipAria, rulesChipTitle, rulesChipLabel,
  FOG_CHIP_INK, AMBER_CAUTION,
} from '../lib/venue-policies-gate.js';

function resolveEventHref_DP(event) {
  if (typeof event.url === 'string' && event.url) return event.url;
  if (typeof event.link === 'string' && event.link) return event.link;
  if (typeof event.slug === 'string' && event.slug) return `/e/${event.slug}`;
  return null;
}

// Strip known TZ artifacts from event.time. Several scrapers (TM Discovery,
// some sitemap-parse paths) stored show times in UTC, producing "3 AM" /
// "4 AM" / "5 AM" strings for 8-11 PM PT shows. Concerts almost never start
// 1-5 AM, so we hide those values until the scraper writes proper local PT
// times. TBD also drops. Bug context: project_merch_engine_2026_06_02 memory.
function cleanEventTime(time) {
  if (!time || time === 'TBD') return null;
  if (/^[1-5]\s*AM$/i.test(time.trim())) return null;
  return time;
}

function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function tokens(darkMode) {
  return {
    onSurface: darkMode ? '#E8E6E2' : '#1A1A1A',
    muted: darkMode ? 'rgba(232,230,226,0.62)' : 'rgba(26,26,26,0.62)',
    amber: darkMode ? '#FFD280' : 'oklch(0.46 0.13 60)',
    ruleColor: darkMode ? 'rgba(255,210,128,0.45)' : 'rgba(139,74,0,0.55)',
    // Component-boundary border (Iris MEDIUM-2, 2026-07-29): a control whose
    // only boundary is its border needs >=3:1 in BOTH modes — ruleColor's
    // light value measures 2.57:1 on white. 0.8 alpha measures 4.35:1 white /
    // 3.54:1 glass; dark is unchanged. Deliberately NOT ruleColor itself:
    // that token also paints the decorative <hr> rules, and a component
    // boundary shouldn't share a decorative token.
    ctrlBorder: darkMode ? 'rgba(255,210,128,0.45)' : 'rgba(139,74,0,0.8)',
    hairline: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
    imgBg: darkMode ? 'oklch(0.22 0.01 70)' : 'oklch(0.965 0.005 70)',
    ctaShadow: darkMode ? '#0a0a0a' : 'oklch(0.35 0.12 60)',
    chipBg: darkMode ? 'rgba(255,210,128,0.10)' : 'rgba(217,148,46,0.10)',
  };
}

function CategoryChip({ label, darkMode }) {
  const t = tokens(darkMode);
  return (
    <span style={{
      display: 'inline-block',
      fontFamily: 'var(--font-display)',
      fontSize: 10, fontWeight: 800,
      letterSpacing: '0.16em', textTransform: 'uppercase',
      color: t.amber,
      padding: '4px 8px',
      border: `1.5px solid ${t.amber}`,
      borderRadius: 2,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// MERCH chip — identical styling + behavior to the event-card chip in
// Events.jsx so users get the SAME morph + popover experience here as on
// the homepage cards. Click captures rect → mounts MerchPopover via portal
// (which is bumped to z=2000 so it overlays the Spotlight panel).
//
// Fallback: if window.MerchPopover hasn't loaded (race on first paint, or
// no-JS), open /merch-preview.html in a centered popup window so the link
// is never dead.
function MerchChipWithPopover({ event, darkMode }) {
  const [triggerRect, setTriggerRect] = React.useState(null);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof window.MerchPopover === 'function') {
      setTriggerRect(e.currentTarget.getBoundingClientRect());
      return;
    }
    if (event.slug) {
      const w = 760, h = 760;
      const left = Math.max(0, (window.screen.availWidth - w) / 2);
      const top = Math.max(0, (window.screen.availHeight - h) / 2);
      const url = `/merch-preview.html?slug=${encodeURIComponent(event.slug)}`;
      window.open(url, `OnSumnMerch_${event.slug}`,
        `popup,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Preview ${event.merch?.artist || 'artist'} merch`}
        title="Click to view merch"
        style={{
          // Visual weight matches CategoryChip (the MUSIC chip next to it):
          // square, 1.5px full-opacity outline, no translucent fill. Color
          // stays merch-brand #D57800 so the two chips read as siblings, not
          // duplicates; the label reads the amber ink token, since #D57800
          // text is 2.95:1 on the light glass. The trailing arrow signals
          // "click for more."
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--amber-gold-ink)',
          background: 'transparent',
          border: '1.5px solid #D57800',
          borderRadius: 2,
          padding: '4px 8px',
          lineHeight: 1.2, minHeight: 24, cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(213,120,0,0.10)' : 'rgba(213,120,0,0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >Merch <span aria-hidden="true">→</span></button>
      {triggerRect && typeof window.MerchPopover === 'function' && (
        <window.MerchPopover
          event={event}
          darkMode={darkMode}
          triggerRect={triggerRect}
          onClose={() => setTriggerRect(null)}
        />
      )}
    </>
  );
}

// ENTRY chip — venue rules + bag policy, immediately LEFT of MERCH
// (info-before-commerce, the Events.jsx card order). Same gate, same
// RulesPopover as the card chip (z=2000 overlays the Spotlight panel, the
// MERCH precedent); the ANATOMY is this pane's square chip (CategoryChip /
// MERCH sibling — 1.5px outline, display face) rather than the card's
// rounded pill, in the rules channel's fog ink. Leading 5×5 square tell
// (caution in drift), NO trailing arrow — square-before vs arrow-after
// stays the MERCH sibling distinction. Gate-null → chip ABSENT, the pane
// byte-identical to before the feature (spec §1). Fallback when
// RulesPopover hasn't loaded (e.g. a page that never ships it): open the
// venue's own policy page — the venue's page IS the truth.
function EntryChipWithPopover({ event, darkMode }) {
  const [triggerRect, setTriggerRect] = React.useState(null);
  const lookup = useVenuePolicies();
  const rulesPolicy = policyChipForEvent(lookup, event);
  if (!rulesPolicy) return null;

  const fog = darkMode ? FOG_CHIP_INK.dark : FOG_CHIP_INK.light;
  const caution = darkMode ? AMBER_CAUTION.dark : AMBER_CAUTION.light;

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof window.RulesPopover === 'function') {
      setTriggerRect(e.currentTarget.getBoundingClientRect());
    } else {
      window.open(rulesPolicy.entry.source_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <button
        type="button"
        className="focus-double-ring"
        onClick={handleClick}
        aria-label={rulesChipAria(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
        aria-haspopup="dialog"
        title={rulesChipTitle(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: fog,
          background: 'transparent',
          border: `1.5px solid ${fog}`,
          borderRadius: 2,
          padding: '4px 8px',
          lineHeight: 1.2, minHeight: 24, cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(122,153,172,0.12)' : 'rgba(122,153,172,0.14)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <span aria-hidden="true" style={{
          width: 5, height: 5, flex: '0 0 auto',
          background: rulesPolicy.state === 'drift-pointer' ? caution : 'currentColor',
        }} />
        ENTRY
      </button>
      {triggerRect && typeof window.RulesPopover === 'function' && (
        <window.RulesPopover
          event={event}
          entry={rulesPolicy.entry}
          state={rulesPolicy.state}
          eventRules={rulesPolicy.eventRules}
          darkMode={darkMode}
          triggerRect={triggerRect}
          onClose={() => setTriggerRect(null)}
        />
      )}
    </>
  );
}

// SHARE chip — sibling to MerchChipWithPopover. Opens the full
// ShareCardPopover (branded PNG + native file-share to IG/TikTok/Stories +
// X/Reddit/Telegram/Facebook deep links), so the Spotlight detail surface
// gets the exact same share experience as the homepage cards. Falls back to
// the legacy native share sheet if the popover bundle hasn't loaded.
// Neutral on-surface outline keeps it distinct from the amber category /
// merch chips beside it.
function ShareChipWithPopover({ event, darkMode }) {
  const [triggerRect, setTriggerRect] = React.useState(null);
  const t = tokens(darkMode);
  const ShareCardPopover = (typeof window !== 'undefined') ? window.ShareCardPopover : null;

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof ShareCardPopover === 'function') {
      setTriggerRect(e.currentTarget.getBoundingClientRect());
      return;
    }
    if (typeof window !== 'undefined' && typeof window.shareEvent === 'function') {
      window.shareEvent({
        title: event.title, slug: event.slug, venue: event.venue, date: event.date,
        toast: (msg) => { if (typeof window.onsumnToast === 'function') window.onsumnToast(msg); },
      });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-label={`Share ${event.title}`}
        title="Share this show"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: t.onSurface, background: 'transparent',
          border: `1.5px solid ${t.onSurface}`, borderRadius: 2,
          padding: '4px 8px', lineHeight: 1.2, minHeight: 24, cursor: 'pointer',
          whiteSpace: 'nowrap', opacity: 0.8,
          transition: 'background 160ms ease, opacity 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.8';
          e.currentTarget.style.background = 'transparent';
        }}
      >Share <span aria-hidden="true">↗</span></button>
      {triggerRect && typeof ShareCardPopover === 'function' && (
        <ShareCardPopover
          event={event}
          darkMode={darkMode}
          triggerRect={triggerRect}
          onClose={() => setTriggerRect(null)}
        />
      )}
    </>
  );
}

function SubgenreChips({ subgenres, darkMode }) {
  if (!Array.isArray(subgenres) || subgenres.length === 0) return null;
  const t = tokens(darkMode);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
      {subgenres.slice(0, 4).map((g, i) => (
        <span key={i} style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11, fontWeight: 600,
          color: t.amber,
          background: t.chipBg,
          padding: '3px 8px',
          borderRadius: 999,
          letterSpacing: '0.02em',
        }}>{g}</span>
      ))}
    </div>
  );
}

function PrimaryCTA({ href, label, host, darkMode, external }) {
  const t = tokens(darkMode);
  if (!href) return null;
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      aria-label={`${label}${host ? ' on ' + host : ''}${external ? ', opens in new tab' : ''}`}
      style={{
        display: 'block', textAlign: 'center', textDecoration: 'none',
        fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 800,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        padding: '14px 18px',
        background: 'var(--international-orange)',
        color: 'var(--on-international-orange)', border: 0, borderRadius: 0,
        boxShadow: `5px 5px 0 0 ${t.ctaShadow}`,
        transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
        marginTop: 16,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translate(2px, 2px)';
        e.currentTarget.style.boxShadow = `3px 3px 0 0 ${t.ctaShadow}`;
        e.currentTarget.style.background = 'var(--international-orange-bright, #FF5717)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translate(0, 0)';
        e.currentTarget.style.boxShadow = `5px 5px 0 0 ${t.ctaShadow}`;
        e.currentTarget.style.background = 'var(--international-orange)';
      }}
    >
      {label} <span aria-hidden="true">→</span>
      {host && (
        <span style={{
          display: 'inline-block',
          marginLeft: 8, paddingLeft: 8,
          borderLeft: '1px solid rgba(16,17,15,0.35)',
          fontWeight: 700, color: 'var(--on-international-orange)',
          fontSize: 11, letterSpacing: '0.08em',
        }}>{host}</span>
      )}
    </a>
  );
}

function EventDetail({ event, darkMode }) {
  const t = tokens(darkMode);
  const href = resolveEventHref_DP(event);
  const isExternal = href && /^https?:\/\//i.test(href);
  const host = isExternal ? hostOf(href) : '';
  const ctaLabel = isExternal ? 'Get tickets' : 'Open event';
  const merch = event.merch && event.merch.shop_url ? event.merch : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Image / poster — fixed-aspect with gradient fallback */}
      <div style={{
        width: '100%',
        aspectRatio: '5 / 3',
        background: event.image
          ? t.imgBg
          : 'linear-gradient(135deg, rgba(217,148,46,0.20), rgba(255,87,23,0.18) 60%, rgba(34,68,84,0.20))',
        border: `1px solid ${t.hairline}`,
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flex: '0 0 auto',
      }}>
        {event.image ? (
          <img
            src={event.image}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 800,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: t.amber, opacity: 0.85,
          }}>OnSumn</span>
        )}
      </div>

      {/* Header — category + (merch) + date right-anchor */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginTop: 16, flexWrap: 'wrap',
      }}>
        {event.category && <CategoryChip label={event.category} darkMode={darkMode} />}
        {event.venue && <EntryChipWithPopover event={event} darkMode={darkMode} />}
        {merch && <MerchChipWithPopover event={event} darkMode={darkMode} />}
        {event.slug && <ShareChipWithPopover event={event} darkMode={darkMode} />}
        <span style={{
          marginLeft: 'auto',
          fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          color: t.onSurface, whiteSpace: 'nowrap',
        }}>
          {(() => {
            const t = cleanEventTime(event.time);
            return event.date + (t ? ` · ${t}` : '');
          })()}
        </span>
      </div>

      {/* Title */}
      <h2 id="search-detail-title" style={{
        fontFamily: 'var(--font-display)', fontWeight: 900,
        fontSize: 'clamp(22px, 3.4vw, 30px)', lineHeight: 1.05,
        letterSpacing: '-0.015em',
        color: t.onSurface, margin: '12px 0 0',
        textWrap: 'balance',
      }}>{event.title}</h2>

      {/* Venue line */}
      {event.venue && (
        <p style={{
          margin: '8px 0 0',
          display: 'flex', gap: 6, alignItems: 'baseline',
          fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
          color: t.muted, lineHeight: 1.4,
        }}>
          <span aria-hidden="true" style={{ color: t.amber, fontWeight: 700 }}>↳</span>
          <span>{event.venue}{event.city ? ` · ${event.city}` : ''}</span>
        </p>
      )}

      {/* Description — clamped to 4 lines */}
      {event.desc && event.desc !== event.title && (
        <p style={{
          margin: '14px 0 0',
          fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.55,
          color: t.onSurface, opacity: 0.85,
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>{event.desc}</p>
      )}

      <SubgenreChips subgenres={event.subgenres} darkMode={darkMode} />

      {/* Amber rule */}
      <hr aria-hidden="true" style={{
        border: 0, height: 1, background: t.ruleColor,
        margin: '20px 0 0',
      }} />

      {/* CTA */}
      <PrimaryCTA href={href} label={ctaLabel} host={host} darkMode={darkMode} external={isExternal} />

      {/* ics-control:start — ADD TO CALENDAR (goal C2, 2026-07-29).
          A REAL ANCHOR to the /e/{slug}.ics endpoint: no click handler, no
          fetch, no blob — survives copy-paste and needs no JS. Gated on
          slug + a resolvable date so it can never point at a 404 (dead links
          are forbidden; iso_date OR startDate mirrors the endpoint's own
          hasCalendarDate — Iris LOW-2). In-row control → 44px floor (Iris).
          Colors are the pane's own theme tokens (t.amber / t.ctrlBorder —
          both dark/light resolved), no literals, no transition. Label +
          aria-label CO-SIGNED by Rhea + Iris 2026-07-29 (label-in-name:
          the visible text leads the accessible name, contiguous). */}
      {event.slug && (event.iso_date || event.startDate) && (
        <a
          href={`/e/${event.slug}.ics`}
          download
          aria-label={`Add to calendar: ${event.title} (.ics file)`}
          style={{
            marginTop: 10,
            minHeight: 44, boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: t.amber, textDecoration: 'none',
            border: `1px solid ${t.ctrlBorder}`, borderRadius: 10,
            padding: '10px 16px',
          }}
        >Add to calendar <span aria-hidden="true">↓</span></a>
      )}
      {/* ics-control:end */}

      {/* No secondary merch link — the MERCH chip in the header opens the
          full MerchPopover (identical to event-card behavior), so a sub-link
          would be redundant. FTC disclosure is inside MerchPopover itself. */}

      {/* OnSumn preview secondary link (only when CTA is external + slug exists) */}
      {isExternal && event.slug && (
        <a href={`/e/${event.slug}`} style={{
          marginTop: 10,
          display: 'block', textAlign: 'center',
          fontFamily: 'var(--font-body)', fontSize: 12,
          color: t.muted, textDecoration: 'underline', textUnderlineOffset: 3,
        }}>View on OnSumn</a>
      )}
    </div>
  );
}

function VenueDetail({ venue, eventCount, darkMode }) {
  const t = tokens(darkMode);
  const href = venue.slug ? `/venue/${venue.slug}` : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        width: '100%',
        aspectRatio: '5 / 3',
        background: 'linear-gradient(135deg, rgba(34,68,84,0.30), rgba(122,153,172,0.20))',
        border: `1px solid ${t.hairline}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span aria-hidden="true" style={{ fontSize: 64, opacity: 0.55 }}>🏟</span>
      </div>

      <div style={{ marginTop: 16 }}>
        <CategoryChip label="Venue" darkMode={darkMode} />
      </div>

      <h2 id="search-detail-title" style={{
        fontFamily: 'var(--font-display)', fontWeight: 900,
        fontSize: 'clamp(22px, 3.4vw, 30px)', lineHeight: 1.05,
        letterSpacing: '-0.015em',
        color: t.onSurface, margin: '12px 0 0',
      }}>{venue.canonical}</h2>

      <p style={{
        margin: '8px 0 0',
        fontFamily: 'var(--font-body)', fontSize: 14,
        color: t.muted, lineHeight: 1.4,
      }}>
        {venue.city || ''}{eventCount ? ` · ${eventCount} upcoming` : ''}
      </p>

      <hr aria-hidden="true" style={{
        border: 0, height: 1, background: t.ruleColor,
        margin: '20px 0 0',
      }} />

      <PrimaryCTA href={href} label="View venue" darkMode={darkMode} external={false} />
    </div>
  );
}

function SearchDetailPane({ item, darkMode }) {
  if (!item) {
    const t = tokens(darkMode);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', minHeight: 240,
        padding: 24,
        color: t.muted,
        fontFamily: 'var(--font-body)', fontSize: 13,
        textAlign: 'center',
      }}>
        <span>Hover a result or use ↑/↓ to preview here.</span>
      </div>
    );
  }
  if (item.kind === 'venue') {
    return <VenueDetail venue={item.venue} eventCount={item.eventCount} darkMode={darkMode} />;
  }
  return <EventDetail event={item.event} darkMode={darkMode} />;
}

window.SearchDetailPane = SearchDetailPane;
